"use client";

import Image from "next/image";
import { useRef, type ReactNode } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "motion/react";

import { Reveal, MARKETING_EASE } from "@/components/marketing/reveal";
import { cn } from "@/lib/utils";

type FeatureRowProps = {
  eyebrow: string;
  title: string;
  body: ReactNode;
  imageSrc: string;
  imageAlt: string;
  imageWidth: number;
  imageHeight: number;
  reverse?: boolean;
};

/**
 * Alternating image/text band. Stacked on mobile, two columns from md up.
 * The phone render slides in from its own side and drifts with scroll parallax
 * over a red glow that lights up as the row enters view; the copy staggers in
 * beside it.
 */
export function FeatureRow({
  eyebrow,
  title,
  body,
  imageSrc,
  imageAlt,
  imageWidth,
  imageHeight,
  reverse = false,
}: FeatureRowProps) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const parallax = useTransform(scrollYProgress, [0, 1], [48, -48]);

  return (
    <section className="mx-auto w-full max-w-6xl overflow-x-clip px-6 py-16 sm:py-24">
      <div
        className={cn(
          "flex flex-col items-center gap-10 md:flex-row md:gap-16",
          reverse && "md:flex-row-reverse"
        )}
      >
        <div
          ref={ref}
          className="relative flex w-full items-center justify-center md:w-1/2"
        >
          {/* Soft radial red glow behind the phone render — lights up on view. */}
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center"
            initial={reduce ? undefined : { opacity: 0.25, scale: 0.85 }}
            whileInView={reduce ? undefined : { opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: MARKETING_EASE }}
          >
            <div className="h-[70%] w-[70%] rounded-full bg-red-600/25 blur-[5rem]" />
          </motion.div>

          <motion.div
            style={reduce ? undefined : { y: parallax }}
            className="flex w-[70%] max-w-sm justify-center md:w-full"
          >
            <motion.div
              initial={
                reduce ? undefined : { opacity: 0, x: reverse ? 60 : -60, scale: 0.95 }
              }
              whileInView={reduce ? undefined : { opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.9, ease: MARKETING_EASE }}
            >
              <Image
                src={imageSrc}
                alt={imageAlt}
                width={imageWidth}
                height={imageHeight}
                sizes="(min-width: 768px) 24rem, 70vw"
                className="h-auto w-full drop-shadow-[0_1.5rem_3rem_rgba(0,0,0,0.6)]"
              />
            </motion.div>
          </motion.div>
        </div>

        <div className="w-full text-center md:w-1/2 md:text-left">
          <Reveal direction={reverse ? "left" : "right"} blur={false}>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-red-500">
              {eyebrow}
            </p>
          </Reveal>
          <Reveal direction={reverse ? "left" : "right"} delay={0.08}>
            <h2 className="mt-3 text-balance text-3xl font-bold text-white sm:text-4xl">
              {title}
            </h2>
          </Reveal>
          <Reveal direction={reverse ? "left" : "right"} delay={0.16}>
            <div className="mt-4 text-base leading-relaxed text-white/65 sm:text-lg">
              {body}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
